<template>
  <div class="dashboard-eco-drivers">
    <div class="eco-drivers-header">
      <h3 class="eco-drivers-title">
        <v-icon small color="#059669">mdi-leaf</v-icon>
        {{ $t('org-dashboard.eco-top-drivers') }}
      </h3>
      <div class="eco-drivers-subtitle">{{ $t('org-dashboard.eco-drivers-subtitle') }}</div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="eco-drivers-loading">
      <v-skeleton-loader type="card" />
    </div>

    <!-- Empty state -->
    <div v-else-if="!drivers || !drivers.length" class="eco-drivers-empty">
      <v-icon color="grey lighten-1" size="40">mdi-leaf</v-icon>
      <p>{{ $t('org-dashboard.peak-no-data') }}</p>
    </div>

    <template v-else>
      <!-- KPI summary -->
      <div class="eco-kpi-grid">
        <div class="eco-kpi-card eco-kpi-card--green">
          <v-icon color="#059669" size="18">mdi-molecule-co2</v-icon>
          <div class="eco-kpi-value">{{ totalCo2Display }}</div>
          <div class="eco-kpi-label">{{ $t('org-dashboard.eco-drivers-total-co2') }}</div>
        </div>
        <div class="eco-kpi-card">
          <v-icon color="#6B7280" size="18">mdi-account-arrow-up</v-icon>
          <div class="eco-kpi-value">{{ avgCo2Display }}</div>
          <div class="eco-kpi-label">{{ $t('org-dashboard.eco-drivers-avg-co2') }}</div>
        </div>
        <div class="eco-kpi-card">
          <v-icon color="#6B7280" size="18">mdi-tree</v-icon>
          <div class="eco-kpi-value">{{ totalTreesDisplay }}</div>
          <div class="eco-kpi-label">{{ $t('org-dashboard.eco-trees-equiv') }}</div>
        </div>
      </div>

      <!-- Trend chart (future backend data) -->
      <div v-if="trend && trend.length" class="eco-trend-section">
        <h4 class="eco-section-title">{{ $t('org-dashboard.eco-drivers-trend') }}</h4>
        <highcharts :options="trendChartOptions" />
      </div>

      <!-- Leaderboard table -->
      <div class="eco-table-section">
        <div class="eco-table-header">
          <h4 class="eco-section-title">{{ $t('org-dashboard.eco-top-drivers') }}</h4>
          <a class="eco-view-all" @click="$emit('view-all')">{{ $t('org-dashboard.eco-drilldown-view-all') }}</a>
        </div>
        <table class="eco-leaderboard">
          <thead>
            <tr>
              <th class="text-center">{{ $t('org-dashboard.eco-rank') }}</th>
              <th>{{ $t('org-dashboard.eco-driver') }}</th>
              <th class="text-right">{{ $t('org-dashboard.eco-co2-saved') }}</th>
              <th class="text-right">{{ $t('org-dashboard.eco-trees-equiv') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(driver, i) in drivers" :key="i">
              <td class="eco-rank text-center">{{ i + 1 }}</td>
              <td>
                <span
                  v-if="driverMemberMap[driver.name]"
                  class="eco-driver-link"
                  @click="$emit('driver-click', driverMemberMap[driver.name])"
                >{{ driver.name }}</span>
                <span v-else>{{ driver.name }}</span>
              </td>
              <td class="text-right eco-value-cell">{{ formatKg(driver.co2SavedKg) }}</td>
              <td class="text-right eco-value-cell">{{ formatDecimal(driver.treesEquivalent) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'

export default {
  name: 'DashboardEcoDrivers',
  components: {
    highcharts: Chart,
  },

  props: {
    drivers: { type: Array, default: () => [] },
    ecoImpact: { type: Object, default: null },
    ecoEquivalents: { type: Object, default: null },
    trend: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    driverMemberMap: { type: Object, default: () => ({}) },
  },

  computed: {
    ourDrivers() {
      return this.ecoImpact?.ourDrivers || null
    },

    totalCo2Display() {
      const kg = this.ourDrivers?.co2SavedKg
      if (kg != null) return this.formatTons(kg / 1000)
      return '-'
    },

    avgCo2Display() {
      const perDriver = this.ecoImpact?.perDriver
      if (perDriver?.co2SavedKg != null) {
        return this.formatTons(perDriver.co2SavedKg / 1000)
      }
      return '-'
    },

    totalTreesDisplay() {
      const trees = this.ourDrivers?.treesEquivalent
      if (trees == null) return '-'
      return Math.round(trees).toLocaleString()
    },

    trendChartOptions() {
      if (!this.trend || !this.trend.length) return null

      const months = this.trend.map(t => t.month || t.label || '')
      const seriesData = []

      // Extract top 3 driver trends if available
      const driverNames = [...new Set(this.trend.flatMap(t => Object.keys(t.drivers || {})))]
      const top3 = driverNames.slice(0, 3)
      const colors = ['#059669', '#3B82F6', '#F59E0B']

      top3.forEach((name, i) => {
        seriesData.push({
          name,
          data: this.trend.map(t => {
            const val = t.drivers && t.drivers[name]
            return val != null ? Math.round(val / 10) / 100 : 0
          }),
          color: colors[i],
        })
      })

      if (!seriesData.length) {
        seriesData.push({
          name: this.$t('org-dashboard.eco-co2-saved'),
          data: this.trend.map(t => Math.round((t.totalCo2Kg || 0) / 10) / 100),
          color: '#059669',
        })
      }

      return {
        chart: {
          type: 'spline',
          height: 220,
          backgroundColor: 'transparent',
          style: { fontFamily: 'inherit' },
        },
        title: { text: null },
        credits: { enabled: false },
        xAxis: {
          categories: months,
          labels: { style: { fontSize: '10px', color: '#9ca3af' } },
          lineColor: '#e5e7eb',
        },
        yAxis: {
          title: { text: null },
          labels: {
            style: { fontSize: '10px', color: '#6b7280' },
            format: '{value} t',
          },
          gridLineColor: '#f1f5f9',
          min: 0,
        },
        tooltip: {
          shared: true,
          valueSuffix: ' t CO₂',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 8,
          style: { fontSize: '11px' },
        },
        plotOptions: {
          spline: { lineWidth: 2, marker: { radius: 3 } },
          series: { animation: { duration: 600 } },
        },
        legend: {
          enabled: seriesData.length > 1,
          itemStyle: { fontSize: '11px', color: '#6b7280' },
        },
        series: seriesData,
      }
    },
  },

  methods: {
    formatTons(tons) {
      if (tons == null) return '-'
      if (tons >= 1000) return Math.round(tons).toLocaleString() + ' t'
      if (tons >= 1) return tons.toFixed(1) + ' t'
      return Math.round(tons * 1000) + ' kg'
    },

    formatDecimal(val) {
      if (val == null) return '-'
      return val.toFixed(2)
    },

    formatKg(val) {
      if (val == null) return '-'
      const tons = val / 1000
      if (tons >= 100) return Math.round(tons).toLocaleString() + ' t'
      if (tons >= 1) return tons.toFixed(2) + ' t'
      return Math.round(val) + ' kg'
    },

  },
}
</script>

<style lang="scss" scoped>
.dashboard-eco-drivers {
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.eco-drivers-header {
  margin-bottom: 20px;
}

.eco-drivers-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 6px;
}

.eco-drivers-subtitle {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
}

.eco-drivers-loading,
.eco-drivers-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 8px;

  p {
    font-size: 13px;
    color: #9ca3af;
    margin: 0;
  }
}

/* KPI summary */
.eco-kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.eco-kpi-card {
  background: #f9fafb;
  border-radius: 10px;
  padding: 14px;
  text-align: center;
  border: 1px solid #f3f4f6;

  &--green {
    background: #ecfdf5;
    border-color: #a7f3d0;
  }
}

.eco-kpi-value {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin-top: 6px;
  line-height: 1.2;
}

.eco-kpi-card--green .eco-kpi-value {
  color: #059669;
}

.eco-kpi-label {
  font-size: 10px;
  color: #6b7280;
  margin-top: 2px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Sections */
.eco-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 12px;
}

.eco-trend-section {
  margin-bottom: 24px;
}

.eco-table-section {
  margin-bottom: 0;
}

.eco-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  .eco-section-title {
    margin-bottom: 0;
  }
}

.eco-view-all {
  font-size: 12px;
  font-weight: 600;
  color: #FF5722;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

/* Leaderboard table */
.eco-leaderboard {
  width: 100%;
  border-collapse: collapse;

  thead th {
    font-size: 10px;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
    padding: 6px 8px;
    border-bottom: 1px solid #f3f4f6;
    white-space: nowrap;
  }

  tbody td {
    padding: 8px;
    font-size: 12px;
    border-bottom: 1px solid #f9fafb;
  }
}

.eco-rank {
  font-weight: 700;
  color: #059669;
  width: 44px;
}

.eco-value-cell {
  font-weight: 600;
  color: #374151;
}

.eco-driver-link {
  color: #FF5722;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
}

@media (max-width: 768px) {
  .eco-kpi-grid,
  .eco-podium {
    grid-template-columns: 1fr;
  }
}
</style>
